/**
 * The overworld engine.
 *
 * Grid movement with interpolation, a camera that follows, and pixel-perfect scaling: everything
 * is drawn once at 1:1 into an offscreen buffer and then blown up by a whole number, so a font
 * pixel is always a clean square and never a blurry rectangle. This is the one detail that
 * separates "pixel art" from "small image, scaled".
 */

import { copy } from '../../core/copy';
import { decodeLook, type Izgled } from '../../core/look';
import { peerOrdinal } from '../../core/posada';
import { SPEECH_BUBBLE, bubbleColors, characterName, characterPose } from '../art/characters';
import { drawText, measureText, wrapText, LINE_HEIGHT } from '../art/font';
import { palette } from '../art/palette';
import { biomeFor } from '../art/palette';
import {
  BANNER,
  BOAT,
  CHEST,
  EASEL,
  LIGHTHOUSE,
  MARKER_DONE,
  SIGN,
  bake,
  heroColors,
  heroLookKey,
  heroPose,
  propArt,
  spriteColors,
  type HeroPhase,
} from '../art/sprites';
import { PASS_SHARE, STEP_MS } from './gait';
import { Uzivo, type LiveBody } from './live';
import { drawWaterSparkle, renderGroundLayer } from './tiles';
import {
  TILE,
  Terrain,
  WATER,
  characterAt,
  isWalkable,
  islandAt,
  markerAt,
  peerAt,
  tileAt,
  type Marker,
  type PlacedCharacter,
  type PlacedPeer,
  type PlacedProp,
  type World,
} from './world';

export type MarkerStatus = 'zakljucan' | 'otvoren' | 'zavrsen' | 'okacen' | 'feedback' | 'popravljen';

export interface MarkerInfo {
  status: MarkerStatus;
  title: string;
  locked: boolean;
  /** True for the single node the SADA card is pointing at. */
  next: boolean;
}

export interface IslandInfo {
  name: string;
  biome: string;
  locked: boolean;
  blocker: string | null;
  done: number;
  total: number;
  /** Shown under the name where the fraction would go, on an island that has no denominator. */
  tagline: string | null;
}

export interface EngineHooks {
  onInteract(nodeId: string): void;
  onIslandTouch(islandId: string): void;
  onMove(x: number, y: number): void;
  /** Somebody else, faced and talked to. */
  onPeer(memberId: string): void;
  /**
   * Where this player is standing, which way they are looking, and whether a step is in flight.
   *
   * Fired only when one of the three actually changes, which is a few times a second while walking
   * and never at all while standing. The map is still not a save file — nothing here is persisted;
   * it goes to the room and is forgotten the moment somebody stops looking.
   */
  onLive?(tile: { x: number; y: number }, facing: Direction, moving: boolean): void;
}

/**
 * Two zoom levels, because the map has two jobs that pull in opposite directions: walking around
 * an island wants the sprites big, and understanding where you are in the archipelago wants to
 * see more than one island at once. One button rather than a compromise that does neither.
 */
export type ViewMode = 'hod' | 'pregled';

const TILES_ACROSS: Record<ViewMode, { desktop: number; mobile: number }> = {
  hod: { desktop: 22, mobile: 12 },
  pregled: { desktop: 52, mobile: 30 },
};

type Direction = 'up' | 'down' | 'left' | 'right';

const DELTA: Record<Direction, [number, number]> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

/**
 * Letters per second while a line of dialogue types itself out.
 *
 * Fast enough that a reader is never waiting on the machine, slow enough that the box reads as
 * somebody speaking rather than as a paragraph appearing. Any press skips straight to the full
 * line, so this is a default, never a delay imposed on somebody who reads quickly.
 */
const TYPE_MS_PER_CHAR = 18;

/** How close you have to be before somebody's speech bubble appears. */
const BUBBLE_RANGE = 5;

/** Rows of text in one box. The schema caps a line at 240 characters so it always fits. */
const DIALOGUE_ROWS = 3;

interface Dialogue {
  character: PlacedCharacter;
  /** Which of the character's messages. */
  line: number;
  /**
   * The message, wrapped and then cut into boxfuls.
   *
   * Paging rather than a taller box, because the box has to fit a phone held in one hand: on a
   * 12-tile-wide viewport a 240-character message wraps to nine rows, and three of them is all
   * that fits above the thumb. Without pages the other six would silently not exist — the message
   * would look finished and the point of it would be missing.
   */
  pages: string[][];
  page: number;
  /** The text width the rows were wrapped for; the box is measured every frame, so this can go stale. */
  wrappedFor: number;
  /** Characters of the CURRENT page revealed so far. */
  revealed: number;
  total: number;
}

export class Engine {
  private ctx: CanvasRenderingContext2D;
  private buffer: HTMLCanvasElement;
  private bufferCtx: CanvasRenderingContext2D;
  private ground: HTMLCanvasElement;
  private raf = 0;
  private lastTime = 0;
  private elapsed = 0;

  private scale = 3;
  private viewW = 320;
  private viewH = 208;

  private tileX: number;
  private tileY: number;
  private fromX: number;
  private fromY: number;
  private moveProgress = 1;
  private facing: Direction = 'up';
  /** Which leg leads. Every completed step hands the lead to the other one. */
  private stepCount = 0;

  private held = new Set<Direction>();
  private queued: Direction | null = null;
  private view: ViewMode = 'hod';
  /**
   * A refused move — into a marker, a locked island or the fog — leaves moveProgress at 1, so the
   * held key would retry it on the very next frame. Without this the hook fires sixty times a
   * second: sixty modal opens, sixty audio nodes. One bump is one bump.
   */
  private blockedUntil = 0;
  private lastBump = '';
  private hero: ReturnType<typeof heroColors>;
  private lookKey: string;
  /** Which set of body rows to pose. Colours live in `hero`; this is the shape they go on. */
  private izgled: Izgled;
  private souvenirs: readonly string[];
  private nudge: { text: string; until: number } | null = null;
  private peers: readonly PlacedPeer[] = [];
  private crowds: ReadonlyMap<string, { x: number; y: number; extra: number }> = new Map();
  /**
   * The people who are here right now, if the room is connected. Null is the ordinary state — not
   * signed in long enough for the crew to exist, no network, nobody about — and in it this class
   * behaves exactly as it did before any of this was written.
   */
  private live: Uzivo | null = null;
  /** Ids the member has chosen not to see. Their own device, their own business, nobody is told. */
  private hidden: ReadonlySet<string> = new Set();
  /** The last thing said to the room, so an unchanged pose is never reported twice. */
  private reported = '';
  /**
   * The conversation currently on screen, if any. While this is set the character cannot move —
   * walking away mid-sentence would leave a box of somebody else's words floating over the sea.
   */
  private talking: Dialogue | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    private world: World,
    private markers: Map<string, MarkerInfo>,
    private islands: Map<string, IslandInfo>,
    look: Izgled,
    souvenirs: readonly string[],
    start: { x: number; y: number; facing?: Direction },
    private hooks: EngineHooks,
  ) {
    this.ctx = canvas.getContext('2d', { alpha: false })!;
    this.buffer = document.createElement('canvas');
    this.bufferCtx = this.buffer.getContext('2d', { alpha: false })!;
    this.hero = heroColors(look);
    this.lookKey = heroLookKey(look);
    this.izgled = look;
    this.souvenirs = souvenirs;
    this.ground = renderGroundLayer(world, (x, y) => {
      const island = islandAt(world, x, y);
      return biomeFor(island ? this.islands.get(island.islandId)?.biome ?? island.biome : undefined);
    });
    // The requested start is "the tile below the next marker", which the generator may well have
    // filled with a rock. Standing inside scenery looks broken, so walk outwards to the nearest
    // tile that is actually free.
    const spawn = nearestFreeTile(world, start);
    this.tileX = spawn.x;
    this.tileY = spawn.y;
    this.fromX = spawn.x;
    this.fromY = spawn.y;
    // Which way they were looking is part of where they were. Without it, somebody who walked left
    // to read a signpost comes back facing away from it — one byte, and it is the difference between
    // a restore and a respawn.
    if (start.facing) this.facing = start.facing;
    this.resize();
  }

  /* ---------------------------------------------------------------- lifecycle */

  start(): void {
    if (this.raf) return;
    this.lastTime = performance.now();
    const loop = (time: number) => {
      const delta = Math.min(64, time - this.lastTime);
      this.lastTime = time;
      this.elapsed += delta;
      this.update(delta);
      this.draw();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /**
   * Swap in fresh node/island state without rebuilding the world. The tilemap only changes when
   * an island appears or its node count does; everything else — a node turning green, the arrow
   * moving to the next step, the souvenir that goes on the moment an island clears — is just
   * what the renderer reads each frame.
   */
  refresh(
    markers: Map<string, MarkerInfo>,
    islands: Map<string, IslandInfo>,
    souvenirs?: readonly string[],
  ): void {
    this.markers = markers;
    this.islands = islands;
    if (souvenirs) this.souvenirs = souvenirs;
  }

  /**
   * A new haircut, without rebuilding the world.
   *
   * `look` used to reach this class only through the constructor, so the map's rebuild effect had to
   * list it as a dependency — which meant saving an appearance regenerated the whole ground layer
   * (tens of thousands of `fillRect`s, a multi-megabyte canvas) *and* re-spawned the character,
   * mid-session, while the member was looking at it. The three fields below are everything `look`
   * touches; the sprite cache is keyed on `lookKey`, so the next frame simply bakes the new face.
   */
  setLook(look: Izgled): void {
    this.hero = heroColors(look);
    this.lookKey = heroLookKey(look);
    this.izgled = look;
  }

  /**
   * The crew, already placed. Arrives separately from `refresh` because it comes from a different
   * request on a slower clock — the map must never wait on other people to draw itself.
   */
  setPeers(
    peers: readonly PlacedPeer[],
    crowds: ReadonlyMap<string, { x: number; y: number; extra: number }>,
  ): void {
    this.peers = peers;
    this.crowds = crowds;
  }

  /**
   * The live room, or null for none.
   *
   * Handed in rather than owned, because its lifetime is the island's and this object's lifetime is
   * the tilemap's — the engine is rebuilt whenever an island comes into view, and tearing down a
   * working socket to redraw a coastline would be absurd.
   */
  setLive(live: Uzivo | null): void {
    this.live = live;
    // Whatever was said before does not count: this room has never heard any of it.
    //
    // Without this, a member who opens the map and does not move is never in a room at all. The
    // first frame runs before the socket exists, `tellTheRoom` notes the pose as already reported,
    // and then nothing changes again — standing still is a perfectly ordinary thing to do while
    // reading a task, and it made the whole feature silently not happen.
    this.reported = '';
  }

  /** Who this member has chosen not to see. */
  setHidden(hidden: ReadonlySet<string>): void {
    this.hidden = hidden;
  }

  setView(mode: ViewMode): void {
    this.view = mode;
    this.resize();
  }

  viewMode(): ViewMode {
    return this.view;
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const cssW = Math.max(160, Math.round(rect.width));
    const cssH = Math.max(120, Math.round(rect.height));
    const target = TILES_ACROSS[this.view];
    const across = cssW < 560 ? target.mobile : target.desktop;
    // Whole-number scaling only. A scale of 2.4 would put half-pixels in the font.
    this.scale = Math.max(1, Math.min(6, Math.round(cssW / (across * TILE))));
    this.viewW = Math.ceil(cssW / this.scale);
    this.viewH = Math.ceil(cssH / this.scale);

    // Whole-number magnification all the way to the device. Blitting a 347px buffer into a
    // 2080px canvas would give a 5.994x factor, and with smoothing off that lands as some glyph
    // columns 6 device pixels wide and their neighbours 5 — visible jitter in every label.
    const dpr = Math.max(1, Math.min(3, Math.round(window.devicePixelRatio || 1)));
    this.canvas.width = this.viewW * this.scale * dpr;
    this.canvas.height = this.viewH * this.scale * dpr;
    this.buffer.width = this.viewW;
    this.buffer.height = this.viewH;
    this.bufferCtx.imageSmoothingEnabled = false;
    this.ctx.imageSmoothingEnabled = false;
    this.rewrapDialogue();
    this.draw();
  }

  /* ---------------------------------------------------------------- input */

  press(direction: Direction): void {
    // A held arrow must not queue up a step behind a conversation and fire it the moment the box
    // closes — that reads as the character walking off on their own.
    if (this.talking) return;
    this.held.add(direction);
    if (this.moveProgress >= 1) this.tryMove(direction);
    else this.queued = direction;
  }

  release(direction: Direction): void {
    this.held.delete(direction);
  }

  releaseAll(): void {
    this.held.clear();
    this.queued = null;
  }

  interact(): void {
    // The button has one job at a time. Mid-conversation it turns the page, and nothing else.
    if (this.talking) {
      this.advanceDialogue();
      return;
    }

    const [dx, dy] = DELTA[this.facing];
    const ax = this.tileX + dx;
    const ay = this.tileY + dy;

    // Whatever is on the tile being faced wins, and neither a peer nor a character is ever placed
    // on a marker's tile — the checker refuses to publish that — so "ahead" is never ambiguous.
    // Facing somebody and pressing A is the oldest verb in the genre.
    const ahead = markerAt(this.world, ax, ay);
    if (ahead) {
      this.hooks.onInteract(ahead.nodeId);
      return;
    }
    const liveAhead = this.liveAt(ax, ay);
    if (liveAhead) {
      this.hooks.onPeer(liveAhead.id);
      return;
    }
    const characterAhead = characterAt(this.world, ax, ay);
    if (characterAhead) {
      this.talkTo(characterAhead);
      return;
    }
    const peerAhead = this.ghostAt(ax, ay);
    if (peerAhead) {
      this.hooks.onPeer(peerAhead.id);
      return;
    }
    const here = markerAt(this.world, this.tileX, this.tileY);
    if (here) {
      this.hooks.onInteract(here.nodeId);
      return;
    }
    const liveHere = this.liveAt(this.tileX, this.tileY);
    if (liveHere) {
      this.hooks.onPeer(liveHere.id);
      return;
    }
    const characterHere = characterAt(this.world, this.tileX, this.tileY);
    if (characterHere) {
      this.talkTo(characterHere);
      return;
    }
    const peerHere = this.ghostAt(this.tileX, this.tileY);
    if (peerHere) {
      this.hooks.onPeer(peerHere.id);
      return;
    }
    const island = islandAt(this.world, this.tileX, this.tileY);
    if (island) this.hooks.onIslandTouch(island.islandId);
  }

  /* ---------------------------------------------------------------- conversation */

  private talkTo(character: PlacedCharacter): void {
    if (character.lines.length === 0) return;
    // Face them. Being spoken to by the back of somebody's head is a small thing that looks broken.
    const dx = character.x - this.tileX;
    const dy = character.y - this.tileY;
    if (dx !== 0 || dy !== 0)
      this.facing =
        Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up';
    this.releaseAll();
    this.talking = this.openLine(character, 0);
  }

  private openLine(character: PlacedCharacter, index: number, page = 0): Dialogue {
    const width = this.dialogueTextWidth();
    const text = (character.lines[index] ?? '').trim();
    const rows = wrapText(text, width, {});
    const pages: string[][] = [];
    for (let at = 0; at < rows.length; at += DIALOGUE_ROWS)
      pages.push(rows.slice(at, at + DIALOGUE_ROWS));
    if (pages.length === 0) pages.push(['']);
    const at = Math.min(page, pages.length - 1);
    return {
      character,
      line: index,
      pages,
      page: at,
      wrappedFor: width,
      revealed: 0,
      total: pages[at]!.reduce((sum, row) => sum + row.length, 0),
    };
  }

  /**
   * Re-wrap a conversation that is open while the window changes size.
   *
   * The rows are computed once when a message opens; the box is measured every frame. Rotating a
   * phone mid-sentence would otherwise leave text wrapped for a box that no longer exists — lines
   * running past the panel, or a third of it empty. Cheap, and it only runs when the width moves.
   */
  private rewrapDialogue(): void {
    const talking = this.talking;
    if (!talking) return;
    const width = this.dialogueTextWidth();
    if (width === talking.wrappedFor) return;
    const done = talking.revealed >= talking.total;
    const next = this.openLine(talking.character, talking.line, talking.page);
    // Somebody who had already read the page should not have to watch it type itself again.
    if (done) next.revealed = next.total;
    this.talking = next;
  }

  /**
   * One press: finish the page if it is still typing, then turn it, then move to the next message,
   * then close.
   *
   * Reveal-then-advance rather than advance-always is the difference between a box you can hurry
   * and a box that eats the thing you were reading when you tapped twice.
   */
  private advanceDialogue(): void {
    const talking = this.talking;
    if (!talking) return;
    if (talking.revealed < talking.total) {
      talking.revealed = talking.total;
      return;
    }
    if (talking.page + 1 < talking.pages.length) {
      talking.page += 1;
      talking.revealed = 0;
      talking.total = talking.pages[talking.page]!.reduce((sum, row) => sum + row.length, 0);
      return;
    }
    const next = talking.line + 1;
    this.talking =
      next < talking.character.lines.length ? this.openLine(talking.character, next) : null;
  }

  /** True while there is more to read — one more page, or one more message. */
  private hasMore(talking: Dialogue): boolean {
    return (
      talking.page + 1 < talking.pages.length || talking.line + 1 < talking.character.lines.length
    );
  }

  /** True while a conversation owns the screen. The map's own controls check this. */
  inConversation(): boolean {
    return this.talking !== null;
  }

  closeDialogue(): void {
    this.talking = null;
  }

  /** Tap or click on the canvas: walk-free shortcut straight to whatever was touched. */
  pointAt(clientX: number, clientY: number): void {
    // A tap anywhere turns the page. Asking somebody to find a small box to tap while a sentence
    // is waiting on them is the wrong trade on a phone.
    if (this.talking) {
      this.advanceDialogue();
      return;
    }

    const rect = this.canvas.getBoundingClientRect();
    const camera = this.camera();
    const worldX = (clientX - rect.left) / this.scale + camera.x;
    const worldY = (clientY - rect.top) / this.scale + camera.y;
    const tx = Math.floor(worldX / TILE);
    const ty = Math.floor(worldY / TILE);

    // Exact tiles first, all four kinds, in the same order `interact` resolves them — a person who
    // can be walked up to and not tapped, or the reverse, is the kind of inconsistency nobody
    // reports and everybody feels. A peer standing directly below a signpost sits inside the
    // marker's own hit tolerance, and tapping a person has to reach the person.
    const exact = markerAt(this.world, tx, ty);
    if (exact) {
      this.hooks.onInteract(exact.nodeId);
      return;
    }
    const liveHit = this.liveAt(tx, ty);
    if (liveHit) {
      this.hooks.onPeer(liveHit.id);
      return;
    }
    const character = characterAt(this.world, tx, ty);
    if (character) {
      this.talkTo(character);
      return;
    }
    const peer = this.ghostAt(tx, ty);
    if (peer) {
      this.hooks.onPeer(peer.id);
      return;
    }
    // Markers are drawn taller than their tile, so accept a hit one tile below the sprite too.
    const tall = markerAt(this.world, tx, ty - 1);
    if (tall) {
      this.hooks.onInteract(tall.nodeId);
      return;
    }
    const island = islandAt(this.world, tx, ty);
    if (island) this.hooks.onIslandTouch(island.islandId);
  }

  say(text: string, ms = 1800): void {
    this.nudge = { text, until: this.elapsed + ms };
  }

  position(): { x: number; y: number } {
    return { x: this.tileX, y: this.tileY };
  }

  /** True once per bump: a held direction against the same obstacle only counts the first time. */
  private bump(key: string): boolean {
    if (this.elapsed < this.blockedUntil && this.lastBump === key) return false;
    this.blockedUntil = this.elapsed + 450;
    this.lastBump = key;
    return true;
  }

  private tryMove(direction: Direction): void {
    this.facing = direction;
    const [dx, dy] = DELTA[direction];
    const nx = this.tileX + dx;
    const ny = this.tileY + dy;

    if (tileAt(this.world, nx, ny) === Terrain.Fog) {
      if (this.bump(`fog:${nx},${ny}`)) this.say('Dalje se vidi kad stigneš.');
      return;
    }

    const marker = markerAt(this.world, nx, ny);
    if (marker) {
      // Bumping into a marker is the same as talking to it. Nobody should have to learn a button.
      if (this.bump(`marker:${marker.nodeId}`)) this.hooks.onInteract(marker.nodeId);
      return;
    }

    // Characters are not solid — the step goes through — but walking into somebody starts the
    // conversation, for the same reason bumping a signpost reads it. `bump` keeps that to once.
    const person = characterAt(this.world, nx, ny);
    if (person && this.bump(`lik:${person.id}`)) {
      this.talkTo(person);
      return;
    }

    const island = islandAt(this.world, nx, ny);
    if (island && this.islands.get(island.islandId)?.locked && !WATER.has(tileAt(this.world, nx, ny))) {
      const info = this.islands.get(island.islandId);
      if (this.bump(`island:${island.islandId}`)) {
        this.say(info?.blocker ?? 'Još ne.');
        this.hooks.onIslandTouch(island.islandId);
      }
      return;
    }

    if (!isWalkable(this.world, nx, ny)) return;

    this.fromX = this.tileX;
    this.fromY = this.tileY;
    this.tileX = nx;
    this.tileY = ny;
    this.moveProgress = 0;
    this.stepCount += 1;
    this.hooks.onMove(nx, ny);
  }

  /**
   * Advance the walk, spending the whole frame.
   *
   * The loop is the point. Stopping the moment a step completes throws away whatever is left of
   * the frame — up to sixteen milliseconds out of every step — and the character stands still for
   * one frame at every tile edge. Sixty times a second that reads as a limp, and it is the kind
   * of stutter you feel long before you can see it.
   */
  private update(delta: number): void {
    // Everybody else walks whatever else is happening. A conversation stops the player, not the
    // island — freezing the other bodies while a box is open would make the place look switched off.
    this.live?.advance(delta);
    this.tellTheRoom();

    if (this.talking) {
      if (this.talking.revealed < this.talking.total)
        this.talking.revealed = Math.min(
          this.talking.total,
          this.talking.revealed + delta / TYPE_MS_PER_CHAR,
        );
      // A conversation can start mid-stride — walking into somebody is how most of them start —
      // so the step in flight is allowed to finish. Freezing it instead leaves the character
      // standing on one leg between two tiles for as long as the box is open. No NEW step begins.
      if (this.moveProgress < 1) this.moveProgress = Math.min(1, this.moveProgress + delta / STEP_MS);
      // The world keeps breathing — water sparkles, the fire flickers — but nobody sets off.
      return;
    }

    let remaining = delta;
    for (let guard = 0; guard < 4; guard++) {
      if (this.moveProgress < 1) {
        const cost = (1 - this.moveProgress) * STEP_MS;
        if (remaining < cost) {
          this.moveProgress += remaining / STEP_MS;
          return;
        }
        remaining -= cost;
        this.moveProgress = 1;
      }
      const next = this.queued ?? [...this.held][this.held.size - 1] ?? null;
      this.queued = null;
      if (!next) return;
      this.tryMove(next);
      // Refused: no step started, so there is nothing left to spend the frame on.
      if (this.moveProgress >= 1) return;
    }
  }

  /**
   * Say where we are, but only when it has changed.
   *
   * Called every frame and almost always does nothing: a standing player produces no traffic at
   * all, and a walking one produces about six frames a second. The comparison is on a string
   * because the alternative is four fields and a stale-copy bug — this runs sixty times a second
   * and being obviously correct is worth more here than being clever.
   */
  private tellTheRoom(): void {
    const hook = this.hooks.onLive;
    if (!hook) return;
    const moving = this.moveProgress < 1;
    const key = `${this.tileX},${this.tileY},${this.facing},${moving ? 1 : 0}`;
    if (key === this.reported) return;
    this.reported = key;
    hook({ x: this.tileX, y: this.tileY }, this.facing, moving);
  }

  /**
   * Somebody who is really here, on this tile.
   *
   * Live bodies are looked up before authored characters and before the standing crew, and never
   * the other way round. A member who happens to be standing on Marko's tile would otherwise be a
   * person you can see and cannot reach, and unlike Marko they cannot be moved by an admin — they
   * are wherever their owner walked them.
   */
  private liveAt(x: number, y: number): { id: string } | undefined {
    const body = this.live?.at(x, y);
    if (!body || this.hidden.has(body.id)) return undefined;
    return body;
  }

  /** A member of the crew standing here, unless they are also here in person. */
  private ghostAt(x: number, y: number): PlacedPeer | undefined {
    const peer = peerAt(this.peers, x, y);
    if (!peer) return undefined;
    if (this.hidden.has(peer.id)) return undefined;
    // Drawn as a walking body instead; the photograph of them is suppressed everywhere at once.
    if (this.live?.has(peer.id)) return undefined;
    return peer;
  }

  /**
   * Which pose to draw. A step is a swing and a landing: the leg passes the standing one over the
   * first half of the tile and plants over the second, and the lead alternates every step, so a
   * full cycle takes two tiles.
   */
  private phase(): HeroPhase {
    if (this.moveProgress >= 1) return 'stand';
    const lead = this.stepCount % 2 === 1;
    if (this.moveProgress < PASS_SHARE) return lead ? 'passA' : 'passB';
    return lead ? 'contactA' : 'contactB';
  }

  /* ---------------------------------------------------------------- drawing */

  /**
   * Where the character is, rounded to a whole pixel — and rounded *once*.
   *
   * Both the camera and the sprite are derived from this single integer. Rounding them separately
   * is the classic pixel-art judder: with an odd viewport the camera's own rounding drifts out of
   * step with the hero's, and the character slides backwards a pixel every few frames while
   * walking in a straight line. Round first, subtract after, and the offset is constant.
   *
   * Interpolation is linear on purpose. Easing each tile separately decelerates into every tile
   * edge, so holding a direction reads as fast-slow-fast-slow rather than as walking.
   */
  private heroPixel(): { x: number; y: number } {
    const t = this.moveProgress;
    return {
      x: Math.round((this.fromX + (this.tileX - this.fromX) * t) * TILE),
      y: Math.round((this.fromY + (this.tileY - this.fromY) * t) * TILE),
    };
  }

  private camera(): { x: number; y: number } {
    const hero = this.heroPixel();
    return cameraOrigin(hero, { w: this.viewW, h: this.viewH }, {
      w: this.world.width * TILE,
      h: this.world.height * TILE,
    });
  }

  private draw(): void {
    const ctx = this.bufferCtx;
    const camera = this.camera();
    ctx.fillStyle = palette.seaDeepShade;
    ctx.fillRect(0, 0, this.viewW, this.viewH);

    ctx.save();
    ctx.translate(-camera.x, -camera.y);

    const view = {
      x0: Math.max(0, Math.floor(camera.x / TILE) - 1),
      y0: Math.max(0, Math.floor(camera.y / TILE) - 1),
      x1: Math.min(this.world.width - 1, Math.ceil((camera.x + this.viewW) / TILE)),
      y1: Math.min(this.world.height - 1, Math.ceil((camera.y + this.viewH) / TILE)),
    };

    ctx.drawImage(
      this.ground,
      view.x0 * TILE,
      view.y0 * TILE,
      (view.x1 - view.x0 + 1) * TILE,
      (view.y1 - view.y0 + 1) * TILE,
      view.x0 * TILE,
      view.y0 * TILE,
      (view.x1 - view.x0 + 1) * TILE,
      (view.y1 - view.y0 + 1) * TILE,
    );

    drawWaterSparkle(ctx, this.world, Math.floor(this.elapsed / 420) % 4, view);
    this.drawLandmarks(ctx, view);
    // Haze goes under the actors: it is meant to obscure the island, not the person looking at it.
    this.drawLockedIslands(ctx, view);
    this.drawActors(ctx, view);
    this.drawIslandLabels(ctx, view);

    ctx.restore();

    this.drawPrompt(ctx);
    this.drawDialogue(ctx);

    this.ctx.imageSmoothingEnabled = false;
    this.ctx.drawImage(
      this.buffer,
      0,
      0,
      this.viewW,
      this.viewH,
      0,
      0,
      this.canvas.width,
      this.canvas.height,
    );
  }

  private drawLandmarks(ctx: CanvasRenderingContext2D, view: { x0: number; y0: number; x1: number; y1: number }): void {
    for (const island of this.world.islands) {
      if (island.locked || !island.landmark) continue;
      const { x, y } = island.landmark;
      if (x < view.x0 - 2 || x > view.x1 + 2 || y < view.y0 - 3 || y > view.y1 + 2) continue;
      const sprite = bake(`landmark:${island.islandId}`, LIGHTHOUSE, spriteColors);
      ctx.drawImage(sprite.canvas, x * TILE, y * TILE - TILE);
    }
  }

  private markerSprite(marker: Marker, info: MarkerInfo | undefined) {
    if (info && (info.status === 'zavrsen' || info.status === 'popravljen'))
      return bake('marker:done', MARKER_DONE, spriteColors);
    if (marker.kind === 'vezba') return bake('marker:easel', EASEL, spriteColors);
    if (marker.kind === 'veliki') return bake('marker:banner', BANNER, spriteColors);
    if (marker.kind === 'riznica') return bake('marker:chest', CHEST, spriteColors);
    return bake('marker:sign', SIGN, spriteColors);
  }

  /** Markers and the hero share one depth sort, so walking behind a signpost looks right. */
  private drawActors(ctx: CanvasRenderingContext2D, view: { x0: number; y0: number; x1: number; y1: number }): void {
    const hero = this.heroPixel();
    type Drawable = { y: number; paint: () => void };
    const queue: Drawable[] = [];

    // Props go into the queue BEFORE the markers. The sort is stable, so on a tile they share the
    // signpost paints last and stays legible — which is also the order the builder draws them in,
    // and the builder must not show the admin the opposite of what ships.
    for (const prop of this.world.props) {
      if (prop.x < view.x0 - 2 || prop.x > view.x1 + 2) continue;
      if (prop.y < view.y0 - 3 || prop.y > view.y1 + 2) continue;
      queue.push({ y: prop.y * TILE, paint: () => this.drawProp(ctx, prop) });
    }

    for (const marker of this.world.markers) {
      if (marker.x < view.x0 - 2 || marker.x > view.x1 + 2) continue;
      if (marker.y < view.y0 - 2 || marker.y > view.y1 + 2) continue;
      const info = this.markers.get(marker.nodeId);
      const sprite = this.markerSprite(marker, info);
      queue.push({
        y: marker.y * TILE,
        paint: () => {
          if (info?.locked) ctx.globalAlpha = 0.45;
          ctx.drawImage(sprite.canvas, marker.x * TILE, marker.y * TILE);
          ctx.globalAlpha = 1;

          if (info?.next) {
            // A single bouncing arrow above the one thing to do next. Never two at once.
            const bob = Math.round(Math.sin(this.elapsed / 260) * 2);
            drawText(ctx, '▾', marker.x * TILE + 5, marker.y * TILE - 12 + bob, {
              color: palette.gold,
              shadow: palette.ink,
            });
          }
          if (info?.status === 'feedback') {
            drawText(ctx, '★', marker.x * TILE + 4, marker.y * TILE - 11, {
              color: palette.gold,
              shadow: palette.ink,
            });
          } else if (info?.status === 'okacen') {
            drawText(ctx, '⏱', marker.x * TILE + 4, marker.y * TILE - 11, {
              color: palette.paper,
              shadow: palette.ink,
            });
          }
        },
      });
    }

    for (const person of this.world.characters) {
      if (person.x < view.x0 - 2 || person.x > view.x1 + 2) continue;
      if (person.y < view.y0 - 2 || person.y > view.y1 + 2) continue;
      queue.push({ y: person.y * TILE, paint: () => this.drawCharacter(ctx, person) });
    }

    for (const peer of this.peers) {
      if (peer.x < view.x0 - 2 || peer.x > view.x1 + 2) continue;
      if (peer.y < view.y0 - 2 || peer.y > view.y1 + 2) continue;
      // Somebody who is here in person is drawn walking instead. Never both: the same person twice
      // on one island, one of them frozen, reads as the map being broken.
      if (this.live?.has(peer.id) || this.hidden.has(peer.id)) continue;
      queue.push({ y: peer.y * TILE, paint: () => this.drawPeer(ctx, peer) });
    }

    for (const body of this.live?.people() ?? []) {
      if (this.hidden.has(body.id)) continue;
      const at = Uzivo.pixelOf(body);
      if (at.x < view.x0 - 2 || at.x > view.x1 + 2) continue;
      if (at.y < view.y0 - 2 || at.y > view.y1 + 2) continue;
      // Sorted on the INTERPOLATED position, like the hero and unlike the standing crew. Sorting a
      // moving body by the tile it is heading for pops it in front of and behind the player twice
      // per step, which is the one artefact that makes a walking figure look like a sprite rather
      // than like somebody in the same place as you.
      queue.push({ y: Math.round(at.y * TILE), paint: () => this.drawLive(ctx, body, at) });
    }

    for (const crowd of this.crowds.values()) {
      if (crowd.x < view.x0 - 2 || crowd.x > view.x1 + 2) continue;
      if (crowd.y < view.y0 - 2 || crowd.y > view.y1 + 2) continue;
      queue.push({
        y: crowd.y * TILE + 1,
        paint: () => {
          // Everybody who did not fit around this marker, as a count rather than a pile. It is a
          // number about a place, not about a person, so it breaks nothing: nobody is ranked by it
          // and it is gone the moment they move on.
          drawText(ctx, `+${crowd.extra}`, crowd.x * TILE + 6, crowd.y * TILE + 16, {
            color: palette.paper,
            shadow: palette.ink,
          });
        },
      });
    }

    queue.push({
      y: hero.y,
      paint: () => {
        const onWater = WATER.has(tileAt(this.world, this.tileX, this.tileY));
        if (onWater) {
          this.drawBoat(ctx, hero.x, hero.y);
          return;
        }
        this.drawShadow(ctx, hero.x, hero.y);
        if (this.view === 'pregled') {
          // Zoomed out the character is a handful of pixels; a blinking ring keeps "you are here"
          // findable without drawing a second map.
          const on = Math.floor(this.elapsed / 420) % 2 === 0;
          if (on) {
            ctx.strokeStyle = palette.gold;
            ctx.lineWidth = 1;
            ctx.strokeRect(hero.x - 3.5, hero.y - 7.5, 23, 29);
          }
        }
        // The pose key carries the souvenirs, so the bake below is automatically distinct for a
        // student who just cleared an island — no reload, the cap is simply on.
        const { key, rows, flip } = heroPose(this.facing, this.phase(), this.izgled, this.souvenirs);
        const sprite = bake(`hero:${this.lookKey}:${key}:${flip ? 'f' : 'n'}`, rows, this.hero, 1, flip);
        // No vertical offset for the bob: it is baked into the pose, so the planted foot stays on
        // the ground while the head rides up. Lifting the whole sprite would lift the foot too.
        ctx.drawImage(sprite.canvas, hero.x, hero.y - 4);
      },
    });

    queue.sort((a, b) => a.y - b.y);
    for (const item of queue) item.paint();
  }

  /** A decoration. Never solid, sometimes animated, always drawn from its own tile upward. */
  private drawProp(ctx: CanvasRenderingContext2D, prop: PlacedProp): void {
    const art = propArt(prop.kind);
    if (!art || art.frames.length === 0) return;
    const index = art.frames.length > 1 ? Math.floor(this.elapsed / 260) % art.frames.length : 0;
    const sprite = bake(`prop:${prop.kind}:${index}`, art.frames[index]!, spriteColors);
    ctx.drawImage(sprite.canvas, prop.x * TILE, prop.y * TILE - art.lift);
  }

  /**
   * Somebody the admin put there.
   *
   * Built out of the same fourteen body rows as the player, so a character can never drift out of
   * proportion with the person standing next to them. They face the way they were placed and they
   * never move — the whole point of a character is that they are exactly where they were put.
   *
   * The bubble is the only thing that says "this one talks", so it appears whenever the player is
   * close enough to act on it and nowhere else: not across the island, and never zoomed out, where
   * a row of bubbles over a village would be the only thing you could see.
   */
  private drawCharacter(ctx: CanvasRenderingContext2D, person: PlacedCharacter): void {
    const pose = characterPose(person.art, person.facing);
    if (!pose) return;
    const x = person.x * TILE;
    const y = person.y * TILE;

    ctx.fillStyle = 'rgba(8,26,44,0.22)';
    ctx.fillRect(x + 3, y + 14, 10, 1);
    ctx.fillRect(x + 2, y + 15, 12, 1);

    const breathe = Math.sin(this.elapsed / 1100 + person.x + person.y) > 0.65 ? 1 : 0;
    const sprite = bake(pose.key, pose.rows, pose.colors, 1, pose.flip);
    ctx.drawImage(sprite.canvas, x, y - 4 - breathe);

    if (this.view === 'pregled') return;

    const near =
      Math.abs(person.x - this.tileX) + Math.abs(person.y - this.tileY) <= BUBBLE_RANGE;
    const faced = this.facedTile();
    const isFaced = faced.x === person.x && faced.y === person.y;

    if (near && !this.talking) {
      const bob = Math.sin(this.elapsed / 420) > 0 ? 0 : 1;
      const bubble = bake('lik:bubble', SPEECH_BUBBLE, bubbleColors);
      ctx.drawImage(bubble.canvas, x + 4, y - 18 - bob);
    }

    // The name only when they are being faced — the same rule as peers, and for the same reason:
    // a nameplate over everybody turns a village back into a list.
    if (!isFaced || this.talking) return;
    const name = characterName(person.art, person.name);
    const width = measureText(name, {});
    panel(ctx, Math.round(x + TILE / 2 - width / 2 - 4), y - 20, width + 8, 14, palette.ink);
    drawText(ctx, name, Math.round(x + TILE / 2 - width / 2), y - 17, { color: palette.gold });
  }

  private facedTile(): { x: number; y: number } {
    const [dx, dy] = DELTA[this.facing];
    return { x: this.tileX + dx, y: this.tileY + dy };
  }

  /**
   * Somebody else.
   *
   * Drawn exactly the way the player is drawn — same sprite, their own colours, their own earned
   * souvenirs — because the whole point is that the map stops being a diagram of a curriculum and
   * becomes a place with people in it. They face the player, they breathe on a slow clock keyed to
   * their own id so a group is never in lockstep, and they never move: this is where their next
   * step is, not a live position.
   *
   * The name only appears when the player is next to them. A map with a nameplate over every
   * person is a list again.
   */
  private drawPeer(ctx: CanvasRenderingContext2D, peer: PlacedPeer): void {
    const x = peer.x * TILE;
    const y = peer.y * TILE;

    ctx.fillStyle = 'rgba(8,26,44,0.20)';
    ctx.fillRect(x + 3, y + 14, 10, 1);
    ctx.fillRect(x + 2, y + 15, 12, 1);

    // A one-pixel idle, offset per person. Enough that a group reads as alive rather than as a
    // row of statues, small enough that nothing appears to be walking.
    const offset = (peerOrdinal(peer.id) % 1000) * 4;
    const breathe = Math.sin((this.elapsed + offset) / 900) > 0.6 ? 1 : 0;

    // Their look, unpacked the same way the player's is: a peer who chose the second body has to
    // appear on the map as the person they chose, not as a recoloured version of the first one.
    const character = decodeLook(peer.look);
    const colors = heroColors(character);
    const { key, rows, flip } = heroPose('down', 'stand', character, peer.souvenirs);
    const sprite = bake(`peer:${peer.look}:${key}`, rows, colors, 1, flip);
    ctx.globalAlpha = 0.92;
    ctx.drawImage(sprite.canvas, x, y - 4 - breathe);
    ctx.globalAlpha = 1;

    // Exactly the person `interact()` would talk to, and never more than one: three peers around
    // one marker are all "nearby", and three nameplates at once collide into an unreadable stack.
    // Facing somebody is also the question the label answers — "who is this" — so it is the right
    // trigger as well as the only one that cannot overlap.
    if (this.view === 'pregled') return;
    const [dx, dy] = DELTA[this.facing];
    if (this.tileX + dx !== peer.x || this.tileY + dy !== peer.y) return;
    const width = measureText(peer.name, {});
    panel(ctx, Math.round(x + TILE / 2 - width / 2 - 4), y - 18, width + 8, 14, palette.ink);
    drawText(ctx, peer.name, Math.round(x + TILE / 2 - width / 2), y - 15, { color: palette.paper });
  }

  /**
   * Somebody who is here right now.
   *
   * Drawn exactly the way the standing crew is drawn — same sprite, their own colours, their own
   * earned souvenirs — with one difference and deliberately only one: they walk. No tag, no ring,
   * no second colour, nothing that says „live". A member never has to learn which kind of person
   * they are looking at, and there is no state in which this feature announces that the island is
   * empty. When nobody is connected the map is simply the map, and that is the whole design.
   */
  private drawLive(ctx: CanvasRenderingContext2D, body: LiveBody, at: { x: number; y: number }): void {
    const x = Math.round(at.x * TILE);
    const y = Math.round(at.y * TILE);
    const moving = body.progress < 1;

    // The same gait as the hero, off the same function: the swing over the first half of the tile,
    // the landing over the second, the lead alternating every step.
    const phase: HeroPhase = !moving
      ? 'stand'
      : body.stepCount % 2 === 1
        ? body.progress < PASS_SHARE
          ? 'passA'
          : 'contactA'
        : body.progress < PASS_SHARE
          ? 'passB'
          : 'contactB';

    const lifted = phase === 'passA' || phase === 'passB';
    const inset = lifted ? 1 : 0;
    ctx.fillStyle = lifted ? 'rgba(8,26,44,0.16)' : 'rgba(8,26,44,0.22)';
    ctx.fillRect(x + 3 + inset, y + 14, 10 - inset * 2, 1);
    ctx.fillRect(x + 2 + inset, y + 15, 12 - inset * 2, 1);

    const character = decodeLook(body.look);
    const colors = heroColors(character);
    const { key, rows, flip } = heroPose(body.facing, phase, character, body.souvenirs);
    const sprite = bake(`peer:${body.look}:${key}:${flip ? 'f' : 'n'}`, rows, colors, 1, flip);
    ctx.drawImage(sprite.canvas, x, y - 4);

    if (this.view === 'pregled') return;

    const near = Math.abs(at.x - this.tileX) + Math.abs(at.y - this.tileY) <= BUBBLE_RANGE;

    // One of the four, over their head, for a couple of seconds. Only within earshot: a gesture is
    // a thing said in a place, and a phrase legible from across the island would be a broadcast.
    if (body.gesture && near) {
      const text = copy.uzivo.gestovi[body.gesture.gest] ?? '';
      const width = measureText(text, {});
      // Kept inside the viewport. „Pogledaću ti rad." is wider than four tiles, so somebody
      // standing at the top of the screen or against its edge would otherwise say half a sentence
      // — and the half that goes missing is the front of it. Nudged rather than repositioned, so
      // the bubble still reads as coming from them.
      const camera = this.camera();
      const boxW = width + 10;
      const bx = clamp(
        Math.round(x + TILE / 2 - boxW / 2),
        camera.x + 3,
        camera.x + this.viewW - boxW - 3,
      );
      const by = Math.max(y - 22, camera.y + 3);
      panel(ctx, bx, by, boxW, 15, palette.accentDark);
      drawText(ctx, text, bx + 5, by + 4, { color: palette.paper });
      return;
    }

    // The name only when they are being faced — the same rule the standing crew obeys, for the same
    // reason: a nameplate over everybody turns an island back into a list. Both tiles of a step
    // count, so a name does not flicker off while somebody shifts their weight.
    const [dx, dy] = DELTA[this.facing];
    const fx = this.tileX + dx;
    const fy = this.tileY + dy;
    const faced =
      (body.tileX === fx && body.tileY === fy) ||
      (moving && body.fromX === fx && body.fromY === fy);
    if (!faced) return;
    const width = measureText(body.name, {});
    panel(ctx, Math.round(x + TILE / 2 - width / 2 - 4), y - 18, width + 8, 14, palette.ink);
    drawText(ctx, body.name, Math.round(x + TILE / 2 - width / 2), y - 15, { color: palette.paper });
  }

  /**
   * The pool under the feet, and the single strongest cue that the character is on the ground
   * rather than in front of it. It is drawn from the tile position and never from the sprite, so
   * the bob slides the body over a shadow that stays exactly where the feet are.
   *
   * It also breathes with the gait: wider and darker on the landing, tighter while the weight is
   * up. One pixel either way, but the difference between contact and float is one pixel.
   */
  private drawShadow(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const phase = this.phase();
    const lifted = phase === 'passA' || phase === 'passB';
    const inset = lifted ? 1 : 0;
    ctx.fillStyle = lifted ? 'rgba(8,26,44,0.16)' : 'rgba(8,26,44,0.24)';
    ctx.fillRect(x + 3 + inset, y + 14, 10 - inset * 2, 1);
    ctx.fillRect(x + 2 + inset, y + 15, 12 - inset * 2, 1);
  }

  /** Crossing open water. The boat bobs on its own clock; the wake only exists while rowing. */
  private drawBoat(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const bob = Math.round(Math.sin(this.elapsed / 500));
    if (this.moveProgress < 1) {
      const [dx, dy] = DELTA[this.facing];
      const fx = x + 8 - dx * 8;
      const fy = y + 13 - dy * 4;
      const flick = Math.floor(this.elapsed / 90) % 2;
      ctx.fillStyle = palette.foam;
      ctx.fillRect(fx - 1, fy, 1, 1);
      ctx.fillRect(fx + 1, fy + (flick ? 1 : -1), 1, 1);
    }
    ctx.drawImage(bake('boat', BOAT, spriteColors).canvas, x, y + bob);
  }

  /**
   * Haze over an island that is not open yet. Painted tile by tile over the land only — a filled
   * rectangle over the bounding box would put a hard-edged blue box on the open sea, which is the
   * opposite of a silhouette in the distance.
   */
  private drawLockedIslands(
    ctx: CanvasRenderingContext2D,
    view: { x0: number; y0: number; x1: number; y1: number },
  ): void {
    ctx.fillStyle = 'rgba(11,51,88,0.55)';
    for (const island of this.world.islands) {
      if (!island.locked) continue;
      // Clipped to what is on screen. A drawn island can be 64×64, and hazing all four thousand
      // of its tiles every frame to paint the two hundred you can see is the kind of cost that
      // only shows up on the phone of the person it matters to.
      const y0 = Math.max(island.bounds.y0 - 1, view.y0);
      const y1 = Math.min(island.bounds.y1 + 1, view.y1);
      const x0 = Math.max(island.bounds.x0 - 1, view.x0);
      const x1 = Math.min(island.bounds.x1 + 1, view.x1);
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const terrain = tileAt(this.world, x, y);
          if (WATER.has(terrain) || terrain === Terrain.Fog) continue;
          ctx.fillRect(x * TILE, y * TILE, TILE, TILE);
        }
      }
    }
  }

  private drawIslandLabels(ctx: CanvasRenderingContext2D, view: { x0: number; y0: number; x1: number; y1: number }): void {
    for (const island of this.world.islands) {
      const info = this.islands.get(island.islandId);
      if (!info) continue;
      const tx = island.label.x;
      const ty = island.label.y;
      if (tx < view.x0 - 6 || tx > view.x1 + 6 || ty < view.y0 - 3 || ty > view.y1 + 3) continue;

      // Zoomed out, the buffer is scaled up less, so labels have to be drawn bigger to survive.
      const ts = this.view === 'pregled' ? 2 : 1;
      const name = info.name;
      // A free island has no denominator, so „0 od 0" would be both wrong and a third number. Its
      // tagline goes there instead — a sentence rather than a hole.
      const sub = info.locked
        ? (info.blocker ?? '')
        : info.total > 0
          ? `${info.done} od ${info.total}`
          : (info.tagline ?? '');
      const nameWidth = measureText(name, { tracking: 1, scale: ts });
      const subWidth = sub ? measureText(sub, { scale: ts }) : 0;
      const boxW = Math.max(nameWidth, subWidth) + 10 * ts;
      const boxH = (sub ? 26 : 17) * ts;
      const bx = Math.round(tx * TILE + TILE / 2 - boxW / 2);
      const by = Math.round(ty * TILE - 4 * ts);

      panel(ctx, bx, by, boxW, boxH, info.locked ? palette.inkSoft : palette.ink);
      drawText(ctx, name, bx + 5 * ts, by + 3 * ts, {
        color: palette.paper,
        tracking: 1,
        scale: ts,
      });
      if (sub)
        drawText(ctx, sub, bx + 5 * ts, by + 14 * ts, {
          color: info.locked ? palette.gold : palette.accent,
          scale: ts,
        });
    }
  }

  /* ---------------------------------------------------------------- the dialogue box */

  private dialogueWidth(): number {
    return Math.min(this.viewW - 12, 300);
  }

  private dialogueTextWidth(): number {
    return this.dialogueWidth() - 16;
  }

  /**
   * The box is exactly as tall as what is in it.
   *
   * A fixed three-row box for a one-row line puts a slab of empty navy over a third of the island
   * for no reason. Sizing to the page costs one line of arithmetic and gives back the map.
   */
  private dialogueBox(rows: number): { x: number; y: number; w: number; h: number } {
    const w = this.dialogueWidth();
    const h = 11 + LINE_HEIGHT * Math.max(1, Math.min(rows, DIALOGUE_ROWS));
    return { x: Math.round((this.viewW - w) / 2), y: this.viewH - h - 8, w, h };
  }

  /**
   * What somebody says, in a box at the bottom of the screen.
   *
   * The reveal is counted across the page's wrapped rows as one continuous string, so a word never
   * appears on row two before row one has finished — the thing that makes a typewriter effect look
   * like a bug instead of like speech.
   */
  private drawDialogue(ctx: CanvasRenderingContext2D): void {
    const talking = this.talking;
    if (!talking) return;
    const rows = talking.pages[talking.page] ?? [];
    const box = this.dialogueBox(rows.length);

    panel(ctx, box.x, box.y, box.w, box.h, palette.ink);

    const name = characterName(talking.character.art, talking.character.name);
    const nameWidth = measureText(name, { tracking: 1 });
    panel(ctx, box.x + 4, box.y - 11, nameWidth + 10, 15, palette.accentDark);
    drawText(ctx, name, box.x + 9, box.y - 7, { color: palette.paper, tracking: 1 });

    let budget = Math.floor(talking.revealed);
    for (const [index, row] of rows.entries()) {
      if (budget <= 0) break;
      const shown = row.slice(0, budget);
      budget -= row.length;
      drawText(ctx, shown, box.x + 8, box.y + 6 + index * LINE_HEIGHT, { color: palette.paper });
    }

    // The blinking cue only appears once there is nothing left to reveal, so it always means
    // "your turn" and never "still typing".
    if (talking.revealed < talking.total) return;
    if (Math.floor(this.elapsed / 380) % 2 !== 0) return;
    if (this.hasMore(talking)) {
      drawText(ctx, '▾', box.x + box.w - 12, box.y + box.h - 12, { color: palette.gold });
      return;
    }
    // The end of the conversation is a filled square, drawn rather than typed: the bitmap font is
    // hand-authored for Serbian Latin and has no glyph for '■', so asking it for one draws nothing
    // at all — the cue would simply be missing on the last box of every conversation.
    ctx.fillStyle = palette.gold;
    ctx.fillRect(box.x + box.w - 11, box.y + box.h - 10, 5, 5);
  }

  /** The "press A" line, and the one-sentence reason an island will not let you in. */
  private drawPrompt(ctx: CanvasRenderingContext2D): void {
    if (this.talking) return;
    if (this.nudge && this.elapsed < this.nudge.until) {
      const text = this.nudge.text;
      const width = measureText(text) + 14;
      const x = Math.round(this.viewW / 2 - width / 2);
      const y = this.viewH - 30;
      panel(ctx, x, y, width, 19, palette.ink);
      drawText(ctx, text, x + 7, y + 4, { color: palette.paper });
      return;
    }

    const [dx, dy] = DELTA[this.facing];
    const marker = markerAt(this.world, this.tileX + dx, this.tileY + dy);
    if (!marker) return;
    const info = this.markers.get(marker.nodeId);
    if (!info) return;
    const label = info.locked ? 'Zaključano' : info.title;
    const trimmed = label.length > 38 ? `${label.slice(0, 37)}…` : label;
    const width = measureText(trimmed) + 14;
    const x = Math.round(this.viewW / 2 - width / 2);
    const y = this.viewH - 30;
    panel(ctx, x, y, width, 19, palette.ink);
    drawText(ctx, trimmed, x + 7, y + 4, { color: info.locked ? palette.paperEdge : palette.paper });
  }
}

/* ------------------------------------------------------------------ helpers */

/**
 * The top-left corner of the viewport, in world pixels, for a hero already rounded to a pixel.
 *
 * Exported for one reason: the property that matters here cannot be seen in a screenshot. Because
 * the hero's screen position is `hero - camera`, and the camera is derived from the hero, any
 * rounding done twice can make that difference go *backwards* by a pixel — the character slips
 * back a step while walking forwards. Flooring a value that already has an integer hero in it
 * keeps the difference constant, and the test walks a hero across every viewport parity to prove
 * it never moves the wrong way.
 */
export function cameraOrigin(
  hero: { x: number; y: number },
  view: { w: number; h: number },
  world: { w: number; h: number },
): { x: number; y: number } {
  const axis = (heroPx: number, viewPx: number, worldPx: number) => {
    if (worldPx <= viewPx) return Math.floor((worldPx - viewPx) / 2);
    return Math.floor(clamp(heroPx + TILE / 2 - viewPx / 2, 0, worldPx - viewPx));
  };
  return { x: axis(hero.x, view.w, world.w), y: axis(hero.y, view.h, world.h) };
}

/**
 * The framed window, in canvas. Exported rather than copied because it is the visual signature of
 * every box in this product — a second implementation is a second thing to keep in step.
 */
export function panel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string): void {
  ctx.fillStyle = palette.black;
  ctx.fillRect(x - 1, y - 1, w + 2, h + 2);
  ctx.fillStyle = fill;
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = 'rgba(255,255,255,0.13)';
  ctx.fillRect(x, y, w, 1);
}

/** Breadth-first walk outwards to the first tile the player can legally stand on. */
function nearestFreeTile(world: World, from: { x: number; y: number }): { x: number; y: number } {
  if (isWalkable(world, from.x, from.y)) return from;
  for (let radius = 1; radius <= 6; radius++) {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;
        const x = from.x + dx;
        const y = from.y + dy;
        if (isWalkable(world, x, y)) return { x, y };
      }
    }
  }
  return from;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

export type { Direction };
